// services/unifiedAuthManager.js

/**
 * Unified Authentication Manager with localStorage
 * 
 * Uses localStorage instead of sessionStorage to persist auth data across tabs
 */

class UnifiedAuthManager {
    constructor() {
      this.storage = localStorage; // Changed from sessionStorage to localStorage
      
      this.storageKeys = {
        // Custom API Response Data
        USER_ID: 'unified_auth_user_id',
        EMAIL: 'unified_auth_email', 
        TENANT_ID: 'unified_auth_tenant_id',
        JTOKEN: 'unified_auth_jtoken',
        USER_ROLES: 'unified_auth_user_roles',
        TENANT: 'unified_auth_tenant',
        FEATURE_USAGE: 'unified_auth_feature_usage',
        USER_PREFERENCE: 'unified_auth_user_preference',
        CANDIDATE_ID: 'unified_auth_candidate_id',
        RESUME_ID: 'unified_auth_resume_id', 
        BROADBEAN_USER_NAME:'unified_auth_broadbean_username',

        
        // Azure AD Response Data
        NAME: 'unified_auth_name',
        PROFILE_IMAGE: 'unified_auth_profile_image',
        AUTH_METHOD: 'unified_auth_method',
        JOB_TITLE: 'unified_auth_job_title',
        DISPLAY_NAME: 'unified_auth_display_name',
        GIVEN_NAME: 'unified_auth_given_name',
        SURNAME: 'unified_auth_surname',
        OFFICE_LOCATION: 'unified_auth_office_location',
        MOBILE_PHONE: 'unified_auth_mobile_phone',
        BUSINESS_PHONES: 'unified_auth_business_phones',
        
        // Combined User Object
        UNIFIED_USER: 'unified_auth_complete_user',
        
        // Session Info
        LAST_LOGIN: 'unified_auth_last_login',
        SESSION_START: 'unified_auth_session_start'
      };
      
      this.init();
    }
  
    init() {
      // Optional: Add session timeout check here
    }
  
    // ============================================
    // STORE METHODS (SET VALUES)
    // ============================================
  
    storeCustomApiResponse(customApiResponse) {
      try {
        const {
          id,
          email,
          tenant_id,
          jtoken,
          user_roles,
          tenant,
          name,
          candidate_id,
          resume_id,
          broadbean_username
        } = customApiResponse;
  
        if (id) this.storage.setItem(this.storageKeys.USER_ID, id.toString());
        if (email) this.storage.setItem(this.storageKeys.EMAIL, email);
        if (tenant_id) this.storage.setItem(this.storageKeys.TENANT_ID, tenant_id.toString());
        if (jtoken) this.storage.setItem(this.storageKeys.JTOKEN, jtoken);
        if (user_roles) this.storage.setItem(this.storageKeys.USER_ROLES, JSON.stringify(user_roles));
        if (tenant) this.storage.setItem(this.storageKeys.TENANT, tenant);
        if (name) this.storage.setItem(this.storageKeys.NAME, name.toString());
        if (candidate_id) this.storage.setItem(this.storageKeys.CANDIDATE_ID, candidate_id.toString());
        if (resume_id) this.storage.setItem(this.storageKeys.RESUME_ID, resume_id.toString());
          if (broadbean_username) this.storage.setItem(this.storageKeys.BROADBEAN_USER_NAME,broadbean_username.toString());

        this.storage.setItem(this.storageKeys.AUTH_METHOD, 'custom');
        this.storage.setItem(this.storageKeys.LAST_LOGIN, new Date().toISOString());
        
        this._updateUnifiedUser();
        return true;
      } catch (error) {
        console.error('Failed to store custom API response:', error);
        return false;
      }
    }
  
    storeAzureUserData(azureUserData) {
      try {
        const {
          name,
          profileImage,
          jobTitle,
          displayName,
          givenName,
          surname,
          email,
          username,
          officeLocation,
          mobilePhone,
          businessPhones,
          homeAccountId,
          tenantId
        } = azureUserData;
  
        if (name) this.storage.setItem(this.storageKeys.NAME, name);
        if (profileImage) this.storage.setItem(this.storageKeys.PROFILE_IMAGE, profileImage);
        if (jobTitle) this.storage.setItem(this.storageKeys.JOB_TITLE, jobTitle);
        if (displayName) this.storage.setItem(this.storageKeys.DISPLAY_NAME, displayName);
        if (givenName) this.storage.setItem(this.storageKeys.GIVEN_NAME, givenName);
        if (surname) this.storage.setItem(this.storageKeys.SURNAME, surname);
        if (officeLocation) this.storage.setItem(this.storageKeys.OFFICE_LOCATION, officeLocation);
        if (mobilePhone) this.storage.setItem(this.storageKeys.MOBILE_PHONE, mobilePhone);
        if (businessPhones) this.storage.setItem(this.storageKeys.BUSINESS_PHONES, JSON.stringify(businessPhones));
        
        if ((email || username) && !this.getEmail()) {
          this.storage.setItem(this.storageKeys.EMAIL, email || username);
        }
        if (homeAccountId && !this.getUserId()) {
          this.storage.setItem(this.storageKeys.USER_ID, homeAccountId);
        }
        if (tenantId && !this.getTenantId()) {
          this.storage.setItem(this.storageKeys.TENANT_ID, tenantId);
        }
        
        this.storage.setItem(this.storageKeys.AUTH_METHOD, 'azure-ad');
        this.storage.setItem(this.storageKeys.LAST_LOGIN, new Date().toISOString());
        
        this._updateUnifiedUser();
        return true;
      } catch (error) {
        console.error('Failed to store Azure user data:', error);
        return false;
      }
    }
  
    storeBothResponses(customData, azureData) {
      const customResult = this.storeCustomApiResponse(customData);
      const azureResult = this.storeAzureUserData(azureData);
      return customResult && azureResult;
    }
  
    // Individual setters
    setUserId(userId) {
      this.storage.setItem(this.storageKeys.USER_ID, userId.toString());
      this._updateUnifiedUser();
    }
  
    setEmail(email) {
      this.storage.setItem(this.storageKeys.EMAIL, email);
      this._updateUnifiedUser();
    }
  
    setJToken(token) {
      this.storage.setItem(this.storageKeys.JTOKEN, token);
      this._updateUnifiedUser();
    }
  
    setUserRoles(roles) {
      this.storage.setItem(this.storageKeys.USER_ROLES, JSON.stringify(roles));
      this._updateUnifiedUser();
    }
  
    setFeatureUsage(features) {
      this.storage.setItem(this.storageKeys.FEATURE_USAGE, JSON.stringify(features));
      this._updateUnifiedUser();
    }
  
    setUserPreferences(preferences) {
      this.storage.setItem(this.storageKeys.USER_PREFERENCE, JSON.stringify(preferences));
      this._updateUnifiedUser();
    }
  
    setProfileImage(imageUrl) {
      this.storage.setItem(this.storageKeys.PROFILE_IMAGE, imageUrl);
      this._updateUnifiedUser();
    }
  
    setJobTitle(title) {
      this.storage.setItem(this.storageKeys.JOB_TITLE, title);
      this._updateUnifiedUser();
    }
  
    setAuthMethod(method) {
      this.storage.setItem(this.storageKeys.AUTH_METHOD, method);
      this._updateUnifiedUser();
    }
  
    // ============================================
    // GET METHODS (RETRIEVE VALUES)
    // ============================================
  
    getCandidateId() {
      return this.storage.getItem(this.storageKeys.CANDIDATE_ID);
    }
    getResumeId() {
      return this.storage.getItem(this.storageKeys.RESUME_ID);
    }
      getBroadbeanUsername() {
      return this.storage.getItem(this.storageKeys.BROADBEAN_USER_NAME);
    }

    getCustomApiData() {
      return {
        id: this.getUserId(),
        email: this.getEmail(),
        tenant_id: this.getTenantId(),
        jtoken: this.getJToken(),
        user_roles: this.getUserRoles(),
        tenant: this.getTenant(),
        name: this.getName(),
        candidate_id: this.getCandidateId(),
        resume_id: this.getResumeId() ,
        broadbean_username:this.getBroadbeanUsername()
      };
    }
  
    getAzureData() {
      return {
        name: this.getName(),
        profileImage: this.getProfileImage(),
        authMethod: this.getAuthMethod(),
        jobTitle: this.getJobTitle(),
        displayName: this.getDisplayName(),
        givenName: this.getGivenName(),
        surname: this.getSurname(),
        officeLocation: this.getOfficeLocation(),
        mobilePhone: this.getMobilePhone(),
        businessPhones: this.getBusinessPhones()
      };
    }
  
    getUnifiedUser() {
      const stored = this.storage.getItem(this.storageKeys.UNIFIED_USER);
      return stored ? JSON.parse(stored) : null;
    }
  
    getUserId() {
      return this.storage.getItem(this.storageKeys.USER_ID);
    }
  
    getEmail() {
      return this.storage.getItem(this.storageKeys.EMAIL);
    }
  
    getTenantId() {
      return this.storage.getItem(this.storageKeys.TENANT_ID);
    }
  
    getJToken() {
      return this.storage.getItem(this.storageKeys.JTOKEN);
    }
  
    getUserRoles() {
      const roles = this.storage.getItem(this.storageKeys.USER_ROLES);
      return roles ? JSON.parse(roles) : [];
    }
  
    getTenant() {
      return this.storage.getItem(this.storageKeys.TENANT);
    }
  
    getFeatureUsage() {
      const features = this.storage.getItem(this.storageKeys.FEATURE_USAGE);
      return features ? JSON.parse(features) : {};
    }
  
    getUserPreferences() {
      const prefs = this.storage.getItem(this.storageKeys.USER_PREFERENCE);
      return prefs ? JSON.parse(prefs) : {};
    }
  
    getName() {
      return this.storage.getItem(this.storageKeys.NAME);
    }
  
    getProfileImage() {
      return this.storage.getItem(this.storageKeys.PROFILE_IMAGE);
    }
  
    getAuthMethod() {
      return this.storage.getItem(this.storageKeys.AUTH_METHOD);
    }
  
    getJobTitle() {
      return this.storage.getItem(this.storageKeys.JOB_TITLE);
    }
  
    getDisplayName() {
      return this.storage.getItem(this.storageKeys.DISPLAY_NAME);
    }
  
    getGivenName() {
      return this.storage.getItem(this.storageKeys.GIVEN_NAME);
    }
  
    getSurname() {
      return this.storage.getItem(this.storageKeys.SURNAME);
    }
  
    getOfficeLocation() {
      return this.storage.getItem(this.storageKeys.OFFICE_LOCATION);
    }
  
    getMobilePhone() {
      return this.storage.getItem(this.storageKeys.MOBILE_PHONE);
    }
  
    getBusinessPhones() {
      const phones = this.storage.getItem(this.storageKeys.BUSINESS_PHONES);
      return phones ? JSON.parse(phones) : [];
    }
  
    getLastLogin() {
      return this.storage.getItem(this.storageKeys.LAST_LOGIN);
    }
  
    // ============================================
    // UTILITY METHODS
    // ============================================
  
    hasFeatureAccess(featurePath) {
      const features = this.getFeatureUsage();
      const parts = featurePath.split('.');
      let current = features;
      
      for (let i = 0; i < parts.length - 1; i++) {
        current = current[parts[i]];
        if (!current) return false;
      }
      
      const lastPart = parts[parts.length - 1];
      if (Array.isArray(current)) {
        return current.some(feature => feature.includes(lastPart));
      }
      
      return !!current[lastPart];
    }
  
    hasRole(roleId) {
      const roles = this.getUserRoles();
      return roles.includes(roleId);
    }
  
    isAuthenticated() {
      return !!(this.getUserId() && this.getEmail());
    }
  
    getUserDisplayName() {
      return this.getDisplayName() || 
             this.getName() || 
             this.getEmail()?.split('@')[0] || 
             'User';
    }
  
    exportAllData() {
      return {
        customApi: this.getCustomApiData(),
        azure: this.getAzureData(),
        unified: this.getUnifiedUser(),
        meta: {
          isAuthenticated: this.isAuthenticated(),
          lastLogin: this.getLastLogin(),
          authMethod: this.getAuthMethod()
        }
      };
    }
  
    clearAll() {
      Object.values(this.storageKeys).forEach(key => {
        this.storage.removeItem(key);
      });
    }
  
    clearCustomData() {
      const customKeys = [
        this.storageKeys.USER_ID,
        this.storageKeys.EMAIL,
        this.storageKeys.TENANT_ID,
        this.storageKeys.JTOKEN,
        this.storageKeys.USER_ROLES,
        this.storageKeys.TENANT,
        this.storageKeys.FEATURE_USAGE,
        this.storageKeys.USER_PREFERENCE,
        this.storageKeys.CANDIDATE_ID,
        this.storageKeys.RESUME_ID,
        this.storageKeys.BROADBEAN_USER_NAME
      ];
      
      customKeys.forEach(key => this.storage.removeItem(key));
      this._updateUnifiedUser();
    }
  
    clearAzureData() {
      const azureKeys = [
        this.storageKeys.NAME,
        this.storageKeys.PROFILE_IMAGE,
        this.storageKeys.JOB_TITLE,
        this.storageKeys.DISPLAY_NAME,
        this.storageKeys.GIVEN_NAME,
        this.storageKeys.SURNAME,
        this.storageKeys.OFFICE_LOCATION,
        this.storageKeys.MOBILE_PHONE,
        this.storageKeys.BUSINESS_PHONES
      ];
      
      azureKeys.forEach(key => this.storage.removeItem(key));
      this._updateUnifiedUser();
    }
  
    // ============================================
    // PRIVATE METHODS
    // ============================================
  
    _updateUnifiedUser() {
      const unifiedUser = {
        ...this.getCustomApiData(),
        ...this.getAzureData(),
        lastLogin: this.getLastLogin(),
        isAuthenticated: this.isAuthenticated(),
        displayName: this.getUserDisplayName()
      };
      
      this.storage.setItem(this.storageKeys.UNIFIED_USER, JSON.stringify(unifiedUser));
    }
  }
  
  // Create and export singleton instance
  const unifiedAuthManager = new UnifiedAuthManager();
  
  export default unifiedAuthManager;
  
  // Also export class for creating new instances if needed
  export { UnifiedAuthManager };